import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Key, Lock, Phone, Hash, FileText, ChevronDown, RefreshCw, ToggleLeft, ToggleRight, CheckCircle, Send, AlertTriangle } from 'lucide-react';

// Templates approved in WhatsApp Manager. Each one's variable count must match
// the templateParams built in backend/utils/whatsappAlerts.js — add a new entry
// here only after the template is approved and the params are updated to match.
const TEMPLATE_OPTIONS = [
    { value: 'order_alert', label: 'order_alert' },
    { value: '', label: 'None — never send billable messages' }
];

const AlertConfigForm = ({ token, API_URL }) => {
    const [config, setConfig] = useState({
        isActive: false,
        phone: '',
        phoneNumberId: '',
        accessToken: '',
        wabaId: '',
        appSecret: '',
        templateName: 'order_alert',
        templateLang: 'en',
        notifyOnNewOrder: true
    });
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false);
    const [testing, setTesting] = useState(false);
    const [templates, setTemplates] = useState(null); // null = not loaded yet
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', message }

    useEffect(() => {
        fetchConfig();
    }, []);

    const authHeader = { headers: { Authorization: `Bearer ${token}` } };

    const fetchConfig = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/admin/alert-config`, authHeader);
            if (res.data) setConfig(prev => ({ ...prev, ...res.data }));
        } catch (err) {
            console.error('Error fetching alert config', err);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        setFeedback(null);
        try {
            const res = await axios.post(`${API_URL}/api/admin/alert-config`, config, authHeader);
            setConfig(prev => ({ ...prev, ...res.data }));
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (err) {
            setFeedback({ type: 'error', message: err.response?.data?.message || 'Failed to save alert configuration' });
        } finally {
            setLoading(false);
        }
    };

    const handleLoadTemplates = async () => {
        setLoadingTemplates(true);
        setFeedback(null);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/alert-config/templates`,
                { wabaId: config.wabaId, accessToken: config.accessToken },
                authHeader
            );
            setTemplates(res.data.templates);
            if (!res.data.templates.length) {
                setFeedback({ type: 'error', message: 'Meta returned no templates for this account — create one in WhatsApp Manager first.' });
            }
        } catch (err) {
            setFeedback({ type: 'error', message: err.response?.data?.message || 'Could not load templates' });
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleTest = async (forceTemplate = false) => {
        setTesting(true);
        setFeedback(null);
        try {
            const res = await axios.post(
                `${API_URL}/api/admin/alert-config/test`,
                { ...config, forceTemplate },
                authHeader
            );
            setFeedback({ type: 'success', message: res.data.message });
        } catch (err) {
            setFeedback({ type: 'error', message: err.response?.data?.message || 'Test message failed' });
        } finally {
            setTesting(false);
        }
    };

    const canTest = config.phone && config.phoneNumberId && config.accessToken;

    return (
        <form onSubmit={handleSave} className="space-y-6 mt-6">
            <div className="flex items-center gap-4">
                <button
                    type="button"
                    onClick={() => setConfig({ ...config, isActive: !config.isActive })}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold transition-all ${config.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                >
                    {config.isActive ? <ToggleRight size={24} /> : <ToggleLeft size={24} />}
                    {config.isActive ? 'WhatsApp Alerts Enabled' : 'WhatsApp Alerts Disabled'}
                </button>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Recipient WhatsApp Number
                </label>
                <div className="relative">
                    <Phone className="absolute left-3 top-3.5 text-gray-400" size={20} />
                    <input
                        type="tel"
                        className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-green-500 outline-none"
                        value={config.phone}
                        onChange={e => setConfig({ ...config, phone: e.target.value })}
                        placeholder="+917041060632"
                        required={config.isActive}
                    />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    Include the country code. This number must also be listed under "To" in Meta's API Setup page.
                </p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number ID
                </label>
                <div className="relative">
                    <Hash className="absolute left-3 top-3.5 text-gray-400" size={20} />
                    <input
                        className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-green-500 outline-none"
                        value={config.phoneNumberId}
                        onChange={e => setConfig({ ...config, phoneNumberId: e.target.value })}
                        placeholder="123456789012345"
                        required={config.isActive}
                    />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    The sender's ID from Meta → WhatsApp → API Setup — a long number, not a phone number.
                </p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Access Token
                </label>
                <div className="relative">
                    <Key className="absolute left-3 top-3.5 text-gray-400" size={20} />
                    <input
                        type="password"
                        className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-green-500 outline-none"
                        value={config.accessToken}
                        onChange={e => setConfig({ ...config, accessToken: e.target.value })}
                        placeholder="EAAG..."
                        required={config.isActive}
                    />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    The temporary token on that page expires in 24 hours. Use a System User token so alerts don't stop overnight.
                </p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    App Secret
                </label>
                <div className="relative">
                    <Lock className="absolute left-3 top-3.5 text-gray-400" size={20} />
                    <input
                        type="password"
                        className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-green-500 outline-none"
                        value={config.appSecret}
                        onChange={e => setConfig({ ...config, appSecret: e.target.value })}
                        placeholder="From Meta → App settings → Basic"
                    />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    Required for the <span className="font-bold text-gray-700">Accept / Reject</span> buttons: it verifies that a button tap genuinely came from Meta. Leave blank and taps are ignored — alerts still send normally.
                </p>
            </div>

            <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                    WhatsApp Business Account ID
                </label>
                <div className="flex gap-3">
                    <div className="relative flex-1">
                        <Hash className="absolute left-3 top-3.5 text-gray-400" size={20} />
                        <input
                            className="w-full pl-10 border-2 border-gray-200 p-3 rounded-xl focus:border-green-500 outline-none"
                            value={config.wabaId}
                            onChange={e => setConfig({ ...config, wabaId: e.target.value })}
                            placeholder="1015062604765235"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={handleLoadTemplates}
                        disabled={loadingTemplates || !config.wabaId || !config.accessToken}
                        className="px-5 py-3 border-2 border-green-200 text-green-700 rounded-xl font-bold hover:bg-green-50 transition-all flex items-center gap-2 disabled:opacity-40 disabled:hover:bg-transparent whitespace-nowrap"
                    >
                        <RefreshCw size={18} className={loadingTemplates ? 'animate-spin' : ''} />
                        {loadingTemplates ? 'Loading...' : 'Load Templates'}
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    Shown above the phone number in Meta → WhatsApp → API Setup. Loading templates fills the list below with what Meta actually has, including the correct language code.
                </p>
            </div>

            <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Template
                    </label>
                    <div className="relative">
                        <FileText className="absolute left-3 top-3.5 text-gray-400 pointer-events-none" size={20} />
                        <select
                            className="w-full pl-10 pr-8 border-2 border-gray-200 p-3 rounded-xl focus:border-green-500 outline-none appearance-none bg-white cursor-pointer"
                            value={`${config.templateName}|${config.templateLang}`}
                            onChange={e => {
                                const [templateName, templateLang] = e.target.value.split('|');
                                setConfig({ ...config, templateName, templateLang: templateLang || 'en' });
                            }}
                        >
                            {templates
                                ? [
                                    ...templates.map(t => (
                                        <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>
                                            {t.name} ({t.language}) · {t.status} · {t.variableCount} var{t.variableCount === 1 ? '' : 's'}
                                        </option>
                                    )),
                                    <option key="none" value="|en">None — never send billable messages</option>
                                ]
                                : TEMPLATE_OPTIONS.map(option => (
                                    <option key={option.value} value={`${option.value}|${config.templateLang}`}>{option.label}</option>
                                ))}
                            {/* Keep a saved value selectable even if it isn't in the loaded list */}
                            {config.templateName && !(templates || []).some(t => t.name === config.templateName && t.language === config.templateLang)
                                && !TEMPLATE_OPTIONS.some(o => o.value === config.templateName) && (
                                    <option value={`${config.templateName}|${config.templateLang}`}>
                                        {config.templateName} ({config.templateLang}) · saved
                                    </option>
                                )}
                        </select>
                        <ChevronDown className="absolute right-3 top-3.5 text-gray-400 pointer-events-none" size={18} />
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Language
                    </label>
                    <input
                        className="w-full border-2 border-gray-200 p-3 rounded-xl focus:border-green-500 outline-none bg-gray-50"
                        value={config.templateLang}
                        onChange={e => setConfig({ ...config, templateLang: e.target.value })}
                        placeholder="en"
                    />
                </div>
            </div>

            {templates && (
                <p className="text-xs text-gray-500 -mt-3">
                    Only templates marked <span className="font-bold text-gray-700">APPROVED</span> can send. Alerts pass <span className="font-bold text-gray-700">6 variables</span>, so pick a template with 6 — any other count is rejected by Meta.
                </p>
            )}
            <p className="text-xs text-gray-500 -mt-3">
                Only used when the free 24-hour window has closed. Leave the name blank to never send billable template messages — alerts will simply fail outside the window.
            </p>

            <label className="flex items-center gap-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={config.notifyOnNewOrder}
                    onChange={e => setConfig({ ...config, notifyOnNewOrder: e.target.checked })}
                    className="w-5 h-5 accent-green-600 rounded"
                />
                <span className="text-sm font-semibold text-gray-700">Alert me when a new order is placed</span>
            </label>

            {feedback && (
                <div className={`flex items-start gap-2 p-3 rounded-xl text-sm font-medium ${feedback.type === 'success' ? 'bg-green-50 text-green-700 border-2 border-green-100' : 'bg-red-50 text-red-700 border-2 border-red-100'}`}>
                    {feedback.type === 'success' ? <CheckCircle size={18} className="shrink-0 mt-0.5" /> : <AlertTriangle size={18} className="shrink-0 mt-0.5" />}
                    <span className="break-words">{feedback.message}</span>
                </div>
            )}

            <div className="flex gap-3">
                <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all flex justify-center items-center gap-2 disabled:opacity-60"
                >
                    {loading ? 'Saving...' : saved ? <><CheckCircle size={20} /> Saved!</> : <><Save size={20} /> Save Configuration</>}
                </button>
                <button
                    type="button"
                    onClick={() => handleTest(false)}
                    disabled={testing || !canTest}
                    className="px-5 py-3 border-2 border-green-200 text-green-700 rounded-xl font-bold hover:bg-green-50 transition-all flex items-center gap-2 disabled:opacity-40 disabled:hover:bg-transparent"
                    title="Send a plain text test — only delivers inside an open 24-hour window"
                >
                    <Send size={18} />
                    {testing ? 'Sending...' : 'Test'}
                </button>
                <button
                    type="button"
                    onClick={() => handleTest(true)}
                    disabled={testing || !canTest || !config.templateName}
                    className="px-5 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 transition-all flex items-center gap-2 disabled:opacity-40 disabled:hover:bg-transparent"
                    title="Send via your approved template — works with no open window"
                >
                    <FileText size={18} />
                    Template
                </button>
            </div>
            <p className="text-xs text-gray-500 -mt-3">
                Meta reports "accepted" before it attempts delivery, so a success message here doesn't guarantee arrival. If <span className="font-bold text-gray-700">Test</span> reports success but nothing appears, no 24-hour window is open — try <span className="font-bold text-gray-700">Template</span>, which is the path real order alerts will use at night.
            </p>
        </form>
    );
};

export default AlertConfigForm;
